import { Injectable } from '@angular/core';
import { SSE } from 'sse.js';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { Task } from '@models/task/task.model';
import { TaskFilter } from '@models/task/taskFilter.model';
import { TaskList } from '@models/task/taskList.model';
import { PriceSuggesterResultsInterface } from '@app/interfaces/priceSuggester/priceSuggester.interface.type';
import { environment } from '../../../environments/environment';
import { PriceSuggesterParams } from '@app/interfaces/priceSuggester/priceSuggesterParams.interface.type';

export interface AssociatedTasksByTagAndLocation {
  region: string;
  city: string;
  keywords: any; // string[];
  tagId: number;
}

@Injectable()
export class TaskService {
  baseHref = `${environment.apiUrl}/tasks`;
  taskRecommendationBaseHref = `${environment.apiUrl}/task-recommendations`;
  censoringBaseHref = `${environment.censoringBaseHref}`;
  eventSource: SSE;
  arrayOfStringsSubjectExplanationPrompt: Subject<string> =
    new Subject<string>();

  constructor(private http: HttpClient) {}

  dynamicTipsImproveTaskDesc(description: string): Observable<any> {
    const url = `${this.baseHref}/dynamic-tips-improve-task-desc`;
    return this.http.post<any>(url, { description });
  }

  priceSuggester(data: PriceSuggesterParams): Observable<any> {
    const limit = data.limit || 40; // Number of tasks to consider in calculation
    const similarityThreshold = data.similarityThreshold || 0.7; // Cosine similarity
    const suggestedLimit = data.suggestedLimit || 40; // Number of tasks to return
    const url = `${this.baseHref}/predict-budget-suggester?limit=${limit}&similarityThreshold=${similarityThreshold}&suggestedLimit=${suggestedLimit}`;
    return this.http.post<any>(url, data);
  }

  // change from observable to Promise
  predictTimeEstimationPrompt(description: string): Promise<any> {
    const url = `${this.baseHref}/predict-time-estimation-prompt-from-desc`;
    return this.http.post<any>(url, { description }).toPromise();
  }

  streamBudgetExplanation(data: {
    taskDesc: string;
    uuid: string;
  }): Observable<any> {
    const url = `${this.baseHref}/stream-budget-explanation-by-prompt`;
    return this.http.post(url, data);
  }

  /* UNTIL STREAMING IS FIXED
  streamBudgetExplanation(data: {
    taskDesc: string;
    uuid: string;
  }): Observable<any> {
    const resultSubject = new Subject<any>();

    const eventSource = new SSE(
      `${this.baseHref}/stream-budget-explanation-by-prompt`,
      {
        headers: { 'Content-Type': 'application/json' },
        payload: JSON.stringify(data),
      },
    );

    eventSource.addEventListener('error', (error: Event | RouterEvent) => {
      console.error('EventSource failed:', error);
      resultSubject.error(error);
    });

    eventSource.addEventListener('message', (e: MessageEvent) => {
      try {
        if (e.data === '[DONE]') {
          resultSubject.complete();
          return eventSource.close();
        }
        const data = JSON.parse(e.data);
        if (data) {
          resultSubject.next(data);
        }
      } catch (err) {
        console.log('stream, err', err);
      }
    });

    eventSource.stream();

    return resultSubject.asObservable();
  }*/

  getTaskDetailsById(id: number, userId?: number): Observable<Task> {
    const url = userId
      ? `${this.baseHref}/details/${id}/${userId}`
      : `${this.baseHref}/details/${id}`;
    return this.http.get<Task>(url);
  }

  getOwnTasksAndAssociated(): Observable<Task[]> {
    const url = `${this.baseHref}/own-and-associated`;
    return this.http.get<Task[]>(url);
  }

  toggleFollow(
    taskId: number,
    userId: number,
    isFollowed: boolean,
  ): Observable<boolean> {
    const url = `${this.baseHref}/toggle-follow`;
    return this.http.post<boolean>(url, { taskId, userId, isFollowed });
  }

  isFollowedByUser(taskId: number, userId: number): Observable<boolean> {
    const url = `${this.baseHref}/check-follow/${taskId}/${userId}`;
    return this.http.get<boolean>(url);
  }

  getOwnTasksWithPendingOffer(status: string): Observable<Task[]> {
    const url = `${this.baseHref}/own/withpendingoffers/${status}`;
    return this.http.get<Task[]>(url);
  }

  getAllOwnPendingTasks(): Observable<Task[]> {
    const url = `${this.baseHref}/all/own/pending`;
    return this.http.get<Task[]>(url);
  }

  getDamageCompensationElligibleTasks(): Observable<Task[]> {
    const url = `${this.baseHref}/damage-compensation-eligible`;
    return this.http.get<Task[]>(url);
  }

  createTask(taskData: Task, params = null): Observable<Task> {
    const url = `${this.baseHref}`;
    return this.http.post<Task>(url, taskData, { params });
  }

  completeTask(finalData: any): Observable<Task> {
    const url = `${this.baseHref}/complete/`;
    return this.http.post<Task>(url, finalData);
  }

  requestCompletionOfTask(taskId: number): Observable<any> {
    const url = `${this.baseHref}/request-completion`;
    return this.http.post<any>(url, { taskId });
  }

  editTask(taskId: number, task: Task): Observable<Task> {
    const url = `${this.baseHref}/edit/${taskId}`;
    return this.http.post<any>(url, task);
  }

  eraseTask(taskId: number) {
    const url = `${this.baseHref}/erase/${taskId}`;
    return this.http.get<any>(url);
  }

  updateTaskImages(taskData): Observable<any> {
    const url = `${this.baseHref}/save-task-images`;
    return this.http.post<any>(url, taskData);
  }

  applyForCancelTask(data: {
    taskId: number;
    desc: string;
    cancellationReason: {
      reason: string;
    };
    refundOfferPriceInc: boolean;
    supressNotifications?: boolean;
  }): Observable<any> {
    const url = `${this.baseHref}/cancel-task`;
    return this.http.post<any>(url, data);
  }

  undoCancelTask(taskId: number): Observable<any> {
    const url = `${this.baseHref}/undo-cancel-task`;
    return this.http.post<any>(url, { taskId });
  }

  respondToCancelTaskRequest(data): Observable<any> {
    const url = `${this.baseHref}/response-cancel-task`;
    return this.http.post<any>(url, data);
  }

  fetchAssociatedTasksByTag(tagId, wordLookup = null): Observable<Task[]> {
    const url = `${this.baseHref}/fetch-associated-tasks-by-tag/${tagId}`;
    let params = new HttpParams();
    if (wordLookup) {
      params = params.set('wordLookup', wordLookup);
    }
    return this.http.get<Task[]>(url, { params });
  }

  fetchAssociatedTasksByTagAndLocation(
    data: AssociatedTasksByTagAndLocation,
  ): Observable<Task[]> {
    const url = `${this.baseHref}/fetch-associated-tasks-by-tag-location`;
    return this.http.post<Task[]>(url, data);
  }

  fetchAssociatedTasksByTerm(term: string): Observable<Task[]> {
    const url = `${this.baseHref}/fetch-associated-tasks-by-term/${term}`;
    return this.http.get<Task[]>(url);
  }

  getFilterTasks(filter: TaskFilter) {
    const url = `${this.baseHref}/filter`;
    let params = new HttpParams();
    Object.keys(filter).forEach((key) => {
      const value = filter[key];
      if (value !== undefined && value !== null && value !== false) {
        if (Array.isArray(value)) {
          value.forEach((v) => {
            params = params.append(key, v);
          });
        } else {
          params = params.append(key, value);
        }
      }
    });
    return this.http.get<TaskList>(url, { params });
  }

  getRecommendedTasks(
    filter: TaskFilter,
  ): Observable<TaskList> {
    const params = new HttpParams()
      .set('prioritizeSignificant', true)
      .set('filter', JSON.stringify(filter));
    const url = `${this.taskRecommendationBaseHref}`;
    return this.http.get<TaskList>(url, { params });
  }

  getMyTasks(paramObj: any): Observable<TaskList> {
    const url = `${this.baseHref}/my-own-tasks`;
    const params = new HttpParams()
      .set('type', paramObj.type)
      .set('pageNumber', paramObj.pageNumber)
      .set('pageSize', paramObj.pageSize);
    return this.http.get<TaskList>(url, { params });
  }

  checkCancelTaskStatus(taskId: number): Observable<boolean> {
    const url = `${this.baseHref}/check-cancel-task-status/${taskId}`;
    return this.http.get<boolean>(url);
  }

  fetchCancelTask(taskId: number): Observable<any> {
    const url = `${this.baseHref}/fetch-cancel-task/${taskId}`;
    return this.http.get<any>(url);
  }

  fetchSensitiveTask(taskId: number): Observable<any> {
    const url = `${this.baseHref}/fetch-sensitive-task/${taskId}`;
    return this.http.get<Task>(url);
  }

  getExampleTasks(mainTagId: number): Observable<any> {
    const url = mainTagId
      ? `${this.baseHref}/example-completed-tasks?mainTagId=${mainTagId}`
      : `${this.baseHref}/example-completed-tasks`;
    return this.http.get<any>(url);
  }

  getPriceSuggestions(
    title: string,
    description: string,
  ): Observable<PriceSuggesterResultsInterface> {
    const url = `${this.baseHref}/budget-suggestions`;
    return this.http.post<PriceSuggesterResultsInterface>(url, {
      title,
      description,
    });
  }

  getPriceSuggestionsInitial(
    title: string,
    description: string,
    suggestedTaskLimit?: number,
  ): Observable<boolean> {
    const url = `${this.baseHref}/budget-suggestions-initial?suggestedLimit=${suggestedTaskLimit}`;
    return this.http.post<boolean>(url, { title, description });
  }

  fetchSimiliarTasks(
    title: string,
    description: string,
    calculationLimit = 15,
    suggestedTaskLimit = 8,
  ): Observable<any> {
    const url = `${this.baseHref}/similiar-tasks?limit=${calculationLimit}&suggestedLimit=${suggestedTaskLimit}`;
    return this.http.post<any>(url, { title, description });
  }

  fetchSimiliarTasksByDesc(
    title: string,
    description: string,
    calculationLimit = 15,
    suggestedTaskLimit = 8,
  ): Observable<any> {
    const url = `${environment.apiUrl}/misc/similiar-tasks-by-title-desc?limit=${calculationLimit}&suggestedLimit=${suggestedTaskLimit}`;
    return this.http.post<any>(url, { title, description });
  }

  improveTaskDescByElaboratingQuestions(task: Task): Observable<any> {
    const url = `${this.baseHref}/improve-task-desc-elaborating-questions`;
    return this.http.post<any>(url, { ...task });
  }

  deleteTask(id: number): Observable<any> {
    const url = `${this.baseHref}/${id}`;
    return this.http.delete<any>(url);
  }

  generateTaskTitle(description: string) {
    const url = `${this.baseHref}/generate-title`;
    return this.http.post<string>(url, { description });
  }

  predictAddressInDesc(text: string): Promise<{
    hasLocation: boolean;
    originalText: string;
    maskedText: string;
  }> {
    return new Promise((resolve, reject) => {
      const url = `${this.censoringBaseHref}/predict-mask`;
      this.http.post<any>(url, { text }).subscribe({
        next: (res) => resolve(res),
        error: (err) => reject(err),
      });
    });
  }

  getOfferRecommendation(
    taskId: number,
  ): Observable<{ offerId: number; explanation: string }> {
    if (!taskId) {
      return;
    }

    const url = `${this.baseHref}/${taskId}/recommendation`;
    return this.http.get<{ offerId: number; explanation: string }>(url);
  }

  reopenTask(taskId: number): Observable<any> {
    const url = `${this.baseHref}/reopen/${taskId}`;
    return this.http.get<any>(url);
  }
}
